import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errMsg, API_BASE_URL } from "../lib/api";
import { useToast } from "../lib/toast";
import { AppShell, Topbar } from "./Layout";
import FileSidebar from "./FileSidebar";
import { Button, Card, Skeleton, EmptyState, Spinner, cx } from "./ui";
import PushLog, {
  Stat,
  buildLog,
  logToText,
  RunProblemBanner,
  OrphanBanner,
} from "./PushLog";
import {
  FUNCTION_META,
  TONE_CLASSES,
  UploadIcon,
  SendIcon,
  CheckIcon,
  CheckCircleIcon,
  FileIcon,
  ListIcon,
  XIcon,
  RefreshIcon,
  BookIcon,
  AlertIcon,
  ChevronRightIcon,
  DownloadIcon,
  TrashIcon,
} from "./ui/Icons";

// Receive Money and Purchases only VOID — Naqood flips status and zeroes
// the total, record stays. Contacts genuinely DELETE — re-querying the id
// afterward returns nothing at all. Different enough (wording, confirm
// copy, and the actual endpoint shape) that each type carries its own path
// builders rather than assuming one URL pattern.
const VOID_CONFIG = {
  "receive-money": {
    mode: "void",
    refLabel: "Reference",
    filename: "receive-money-voidable-rows.xlsx",
    singlePath: (type, rowId) => `/api/naqood/${type}/void/${rowId}`,
    listPath: (type, functionId) => `/api/naqood/${type}/void-list/${functionId}`,
    bulkPath: (type, functionId) => `/api/naqood/${type}/void-bulk/${functionId}`,
  },
  "spend-money": {
    mode: "void",
    refLabel: "Reference",
    filename: "spend-money-voidable-rows.xlsx",
    singlePath: (type, rowId) => `/api/naqood/${type}/void/${rowId}`,
    listPath: (type, functionId) => `/api/naqood/${type}/void-list/${functionId}`,
    bulkPath: (type, functionId) => `/api/naqood/${type}/void-bulk/${functionId}`,
  },
  purchases: {
    mode: "void",
    refLabel: "PurchaseRef",
    filename: "purchases-voidable-rows.xlsx",
    singlePath: (type, rowId) => `/api/naqood/${type}/void/${rowId}`,
    listPath: (type, functionId) => `/api/naqood/${type}/void-list/${functionId}`,
    bulkPath: (type, functionId) => `/api/naqood/${type}/void-bulk/${functionId}`,
  },
  invoices: {
    mode: "void",
    refLabel: "InvoiceRef",
    filename: "invoices-voidable-rows.xlsx",
    singlePath: (type, rowId) => `/api/naqood/${type}/void/${rowId}`,
    listPath: (type, functionId) => `/api/naqood/${type}/void-list/${functionId}`,
    bulkPath: (type, functionId) => `/api/naqood/${type}/void-bulk/${functionId}`,
  },
  journals: {
    mode: "void",
    refLabel: "JournalRef",
    filename: "journals-voidable-rows.xlsx",
    singlePath: (type, rowId) => `/api/naqood/${type}/void/${rowId}`,
    listPath: (type, functionId) => `/api/naqood/${type}/void-list/${functionId}`,
    bulkPath: (type, functionId) => `/api/naqood/${type}/void-bulk/${functionId}`,
  },
  "journal-transfer": {
    mode: "void",
    refLabel: "JournalRef",
    filename: "journal-transfers-voidable-rows.xlsx",
    singlePath: (type, rowId) => `/api/naqood/${type}/void/${rowId}`,
    listPath: (type, functionId) => `/api/naqood/${type}/void-list/${functionId}`,
    bulkPath: (type, functionId) => `/api/naqood/${type}/void-bulk/${functionId}`,
  },
  contacts: {
    mode: "delete",
    refLabel: "Name",
    filename: "contacts-deletable-rows.xlsx",
    singlePath: (type, rowId) => `/api/naqood/${type}/${rowId}`,
    listPath: (type, functionId) => `/api/naqood/${type}/delete-list/${functionId}`,
    bulkPath: (type, functionId) => `/api/naqood/${type}/delete-bulk/${functionId}`,
  },
};

const fmtDuration = (ms) =>
  ms == null ? "" : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)}s`;

const fmtWhen = (d) =>
  new Date(d).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });

export default function FunctionPage() {
  const { fileId, functionId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const inputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [user, setUser] = useState(null);
  const [fn, setFn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upload");

  const [excelFile, setExcelFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [rowCount, setRowCount] = useState(null);
  const [sheetIssues, setSheetIssues] = useState(null);
  const [downloadingIssues, setDownloadingIssues] = useState(false);

  const [pushing, setPushing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);

  // Voiding is only meaningful for Receive Money — Naqood's only "undo" for a
  // pushed journal entry is voidJournalEntry, not a real delete.
  const [voidedIds, setVoidedIds] = useState(() => new Set());
  const [voidingId, setVoidingId] = useState(null);

  // Bulk void: download the current voidable rows, trim the sheet down to
  // what should actually go, upload it back.
  const [voidListDownloading, setVoidListDownloading] = useState(false);
  const [voidBulkFile, setVoidBulkFile] = useState(null);
  const [voidBulkDragging, setVoidBulkDragging] = useState(false);
  const [voidBulkUploading, setVoidBulkUploading] = useState(false);
  const [voidBulkResult, setVoidBulkResult] = useState(null);
  const voidBulkInputRef = useRef(null);

  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [openRun, setOpenRun] = useState(null);

  const type = fn?.type;
  const meta =
    FUNCTION_META[type] || { Icon: ListIcon, tone: "slate", label: type };

  /* -------------------------------------------------------------- load */

  useEffect(() => {
    Promise.all([
      api.get("/api/auth/me"),
      api.get(`/api/files/${fileId}`),
      api.get(`/api/file-functions/${fileId}/function/${functionId}`),
    ])
      .then(([u, f, func]) => {
        setUser(u.data);
        setFile(f.data);
        setFn(func.data);
        setUploaded(func.data.isUploaded === true);
      })
      .catch((err) => toast.error("Could not load function", errMsg(err)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, functionId]);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const res = await api.get(`/api/push-runs/function/${functionId}`);
      setRuns(res.data);
    } catch (err) {
      toast.error("Could not load history", errMsg(err));
    } finally {
      setRunsLoading(false);
    }
  }, [functionId, toast]);

  useEffect(() => {
    if (tab === "history") loadRuns();
  }, [tab, loadRuns]);

  /* Elapsed-time ticker so a long push never looks frozen. */
  useEffect(() => {
    if (!pushing) return undefined;
    setElapsed(0);
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [pushing]);

  /* ------------------------------------------------------------ upload */

  const handleUpload = async () => {
    if (!excelFile) {
      toast.error("No file selected", "Choose an .xlsx, .xls or .csv file first.");
      return;
    }
    const form = new FormData();
    form.append("file", excelFile);
    try {
      setUploading(true);
      setSheetIssues(null);
      const res = await api.post(
        `/api/import-excel/${functionId}/upload/${type}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const count = res.data?.count ?? 0;
      setRowCount(count);
      setUploaded(true);
      setExcelFile(null);
      setTab("push");
      toast.success(
        "Sheet uploaded",
        `${count} row${count === 1 ? "" : "s"} read and matched to Naqood fields.`
      );
    } catch (err) {
      // A validation failure comes back with a per-row report — show it in full
      // instead of a single toast line.
      const data = err.response?.data;
      if (data?.issues?.length) {
        setSheetIssues({
          issues: data.issues,
          validCount: data.validCount,
          totalRows: data.totalRows,
        });
        toast.error(
          `${data.issues.length} row${data.issues.length === 1 ? "" : "s"} need fixing`,
          "Nothing was imported — see the list below."
        );
      } else {
        toast.error("Upload failed", errMsg(err));
      }
    } finally {
      setUploading(false);
    }
  };

  /* -------------------------------------------------------------- push */

  const handlePush = async () => {

    try {
      setPushing(true);
      setResult(null);
      const res = await api.post(`/api/naqood/${type}/push/${functionId}`);
      const p = res.data?.pushed || {};

      const summary = {
        totalRows: p.totalRows ?? p.total ?? 0,
        alreadySynced: p.alreadySynced ?? 0,
        attempted: p.attempted ?? p.total ?? 0,
        success: p.success ?? 0,
        failed: p.failed ?? [],
        pushed: p.pushed ?? [],
        orphaned: p.orphaned ?? [],
        status: "completed",
      };
      setResult(summary);

      if (summary.failed.length === 0) {
        toast.success(
          "Pushed to Naqood",
          `${summary.success} of ${summary.attempted} row${
            summary.attempted === 1 ? "" : "s"
          } created.`
        );
      } else {
        toast.error(
          `${summary.failed.length} row${summary.failed.length === 1 ? "" : "s"} failed`,
          `${summary.success} succeeded. Full errors are in the log.`
        );
      }
    } catch (err) {
      setResult({
        totalRows: 0,
        alreadySynced: 0,
        attempted: 0,
        success: 0,
        pushed: [],
        failed: [],
        status: "failed",
        error: errMsg(err),
      });
      toast.error("Push failed", errMsg(err));
    } finally {
      setPushing(false);
    }
  };

  const handleVoid = async (rowId) => {
    const cfg = VOID_CONFIG[type];
    const isDelete = cfg?.mode === "delete";
    const confirmMsg = isDelete
      ? "Delete this contact in Naqood? This actually removes the record — it will not be there any more, and this cannot be undone."
      : "Void this entry in Naqood? It stays on the books but nets to zero — nothing is erased, and this cannot be undone from here.";
    if (!window.confirm(confirmMsg)) return;

    setVoidingId(rowId);
    try {
      await api.delete(cfg.singlePath(type, rowId));
      setVoidedIds((prev) => new Set(prev).add(rowId));
      toast.success(
        isDelete ? "Deleted in Naqood" : "Voided in Naqood",
        isDelete ? "The record is gone." : "It stays on the books but nets to zero."
      );
    } catch (err) {
      toast.error(isDelete ? "Could not delete" : "Could not void", errMsg(err));
    } finally {
      setVoidingId(null);
    }
  };

  const downloadVoidList = async () => {
    const cfg = VOID_CONFIG[type];
    try {
      setVoidListDownloading(true);
      const res = await api.get(
        cfg.listPath(type, functionId),
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = cfg?.filename || "rows.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed", errMsg(err));
    } finally {
      setVoidListDownloading(false);
    }
  };

  const handleVoidBulkUpload = async () => {
    if (!voidBulkFile) return;
    const cfg = VOID_CONFIG[type];
    const isDelete = cfg?.mode === "delete";
    const form = new FormData();
    form.append("file", voidBulkFile);
    try {
      setVoidBulkUploading(true);
      setVoidBulkResult(null);
      const res = await api.post(
        cfg.bulkPath(type, functionId),
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setVoidBulkResult(res.data);
      setVoidBulkFile(null);
      const { success, attempted } = res.data;
      const verb = isDelete ? "deleted" : "voided";
      if (res.data.failed?.length) {
        toast.error(
          `${res.data.failed.length} of ${attempted} could not be ${verb}`,
          `${success} ${verb}. Full detail is in the log.`
        );
      } else {
        toast.success(
          isDelete ? "Deleted in Naqood" : "Voided in Naqood",
          `${success} of ${attempted} row${attempted === 1 ? "" : "s"} ${verb}.`
        );
      }
    } catch (err) {
      toast.error(isDelete ? "Bulk delete failed" : "Bulk void failed", errMsg(err));
    } finally {
      setVoidBulkUploading(false);
    }
  };

  const copyLog = (entries) =>
    navigator.clipboard?.writeText(logToText(entries)).then(
      () => toast.success("Copied", `${entries.length} lines copied.`),
      () => toast.error("Copy failed", "Select and copy manually.")
    );

  const downloadLog = (entries, suffix) => {
    const blob = new Blob([logToText(entries)], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${type}-${suffix}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Sheet problems come back as a workbook rather than a text file — one line
   * per problem, sortable and filterable, so a sheet with dozens of bad rows
   * can be worked through instead of read. The server builds it (exceljs is
   * already there for the templates) and we just save the bytes.
   */
  const downloadIssues = async () => {
    if (!sheetIssues?.issues?.length) return;
    try {
      setDownloadingIssues(true);
      const res = await api.post(
        `/api/issues-report/${type}`,
        { issues: sheetIssues.issues },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}-rows-to-fix.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed", errMsg(err));
    } finally {
      setDownloadingIssues(false);
    }
  };

  /* ------------------------------------------------------------ render */

  if (loading) {
    return (
      <AppShell sidebar={<FileSidebar file={null} user={null} fileId={fileId} />}>
        <Topbar title="Loading…" />
        <main className="flex-1 space-y-6 px-8 py-7">
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-72 w-full rounded-xl" />
        </main>
      </AppShell>
    );
  }

  const { Icon } = meta;
  const TABS = [
    { id: "upload", label: "Upload", icon: UploadIcon, done: uploaded },
    { id: "push", label: "Push", icon: SendIcon, disabled: !uploaded },
    { id: "history", label: "History", icon: BookIcon },
    ...(VOID_CONFIG[type]
      ? [{ id: "void", label: VOID_CONFIG[type].mode === "delete" ? "Delete" : "Void", icon: TrashIcon }]
      : []),
  ];

  return (
    <AppShell sidebar={<FileSidebar file={file} user={user} fileId={fileId} activeType={type} />}>
      <Topbar
        title={meta.label}
        orgSlug={file?.naqood?.organizationSlug}
        onBack={() => navigate(`/file/${fileId}`)}
        backTo={`/file/${fileId}`}
      />

      {/* Tabs */}
      <div className="sticky top-[69px] z-20 border-b border-ink-200/80 bg-white/85 px-8 backdrop-blur-md">
        <div className="flex items-center gap-1">
          <span
            className={cx(
              "mr-3 grid h-7 w-7 place-items-center rounded-lg ring-1",
              TONE_CLASSES[meta.tone]
            )}
          >
            <Icon size={14} />
          </span>
          {TABS.map((t) => (
            <button
              key={t.id}
              disabled={t.disabled}
              onClick={() => setTab(t.id)}
              className={cx(
                "relative flex items-center gap-1.5 px-3.5 py-3 text-[13.5px] font-medium transition",
                t.disabled
                  ? "cursor-not-allowed text-ink-300"
                  : tab === t.id
                  ? "text-brand-600"
                  : "text-ink-500 hover:text-ink-800"
              )}
            >
              <t.icon size={15} />
              {t.label}
              {t.done && t.id === "upload" && (
                <CheckIcon size={13} strokeWidth={3} className="text-emerald-500" />
              )}
              {tab === t.id && (
                <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-brand-500" />
              )}
            </button>
          ))}
        </div>
      </div>

      <main className="flex-1 px-8 py-6">
        {/* ------------------------------------------------ UPLOAD */}
        {tab === "upload" && (
          <div className="mx-auto max-w-3xl animate-fade-in space-y-4">
            {/* Start from the right sheet rather than hunting for it. */}
            <div className="flex items-center gap-3 rounded-xl border border-ink-200 bg-white px-4 py-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600 ring-1 ring-brand-100">
                <FileIcon size={17} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-medium text-ink-900">
                  Need the template?
                </p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-ink-500">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-sm border border-[#BF8F00] bg-[#FFE699]" />
                    Mandatory
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-sm border border-[#BFBFBF] bg-[#EDEDED]" />
                    Optional
                  </span>
                  <span className="text-ink-400">
                    — hover a column header in Excel for the rule
                  </span>
                </p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                icon={DownloadIcon}
                onClick={() =>
                  window.open(`${API_BASE_URL}/api/templates/${type}`, "_blank")
                }
              >
                Download
              </Button>
            </div>

            <Card className="p-6">
              {uploaded ? (
                <div className="flex flex-wrap items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3.5">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-emerald-100 text-emerald-700">
                    <CheckCircleIcon size={18} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13.5px] font-medium text-emerald-900">
                      Sheet uploaded and mapped
                    </p>
                    <p className="text-[12.5px] text-emerald-700">
                      {rowCount !== null
                        ? `${rowCount} row${rowCount === 1 ? "" : "s"} ready to push.`
                        : "Ready to push."}
                    </p>
                  </div>
                  <Button size="sm" variant="secondary" icon={RefreshIcon}
                    onClick={() => { setUploaded(false); setResult(null); setRowCount(null); }}>
                    Upload another
                  </Button>
                  <Button size="sm" icon={SendIcon} onClick={() => setTab("push")}>
                    Go to Push
                  </Button>
                </div>
              ) : (
                <>
                  <div
                    onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragOver={(e) => e.preventDefault()}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragging(false);
                      const f = e.dataTransfer.files?.[0];
                      if (f) setExcelFile(f);
                    }}
                    onClick={() => inputRef.current?.click()}
                    className={cx(
                      "flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-12 text-center transition",
                      dragging
                        ? "border-brand-400 bg-brand-50"
                        : excelFile
                        ? "border-brand-300 bg-brand-50/40"
                        : "border-ink-300 bg-ink-50/50 hover:border-brand-300 hover:bg-brand-50/30"
                    )}
                  >
                    <input ref={inputRef} type="file" accept=".xls,.xlsx,.csv"
                      className="hidden"
                      onChange={(e) => setExcelFile(e.target.files[0])} />
                    <span className={cx(
                      "mb-3 grid h-12 w-12 place-items-center rounded-xl ring-1",
                      excelFile
                        ? "bg-brand-100 text-brand-600 ring-brand-200"
                        : "bg-white text-ink-400 ring-ink-200"
                    )}>
                      {excelFile ? <FileIcon size={22} /> : <UploadIcon size={22} />}
                    </span>
                    {excelFile ? (
                      <>
                        <p className="text-[14px] font-medium text-ink-900">{excelFile.name}</p>
                        <p className="mt-0.5 text-[12.5px] text-ink-500">
                          {(excelFile.size / 1024).toFixed(0)} KB · click to change
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-[14px] font-medium text-ink-800">
                          Drop your file here, or click to browse
                        </p>
                        <p className="mt-0.5 text-[12.5px] text-ink-500">
                          .xlsx, .xls or .csv — columns are detected by name
                        </p>
                      </>
                    )}
                  </div>

                  <div className="mt-4 flex items-center gap-2.5">
                    <Button icon={UploadIcon} onClick={handleUpload}
                      loading={uploading} disabled={!excelFile}>
                      {uploading ? "Uploading…" : "Upload sheet"}
                    </Button>
                    {excelFile && !uploading && (
                      <Button variant="ghost" icon={XIcon} onClick={() => setExcelFile(null)}>
                        Clear
                      </Button>
                    )}
                  </div>
                </>
              )}
            </Card>

            {/* Per-row problems found in the sheet — nothing was imported. */}
            {sheetIssues && (
              <Card className="overflow-hidden border-red-200">
                <div className="flex flex-wrap items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-3">
                  <AlertIcon size={16} className="text-red-600" />
                  <p className="text-[13px] font-semibold text-red-900">
                    {sheetIssues.issues.length} of {sheetIssues.totalRows} rows
                    need fixing
                  </p>
                  <span className="text-[12.5px] text-red-700">
                    · {sheetIssues.validCount} row
                    {sheetIssues.validCount === 1 ? "" : "s"} are fine · nothing
                    was imported
                  </span>
                  <div className="ml-auto">
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={DownloadIcon}
                      loading={downloadingIssues}
                      onClick={downloadIssues}
                    >
                      Download
                    </Button>
                  </div>
                </div>

                <div className="max-h-[420px] overflow-y-auto">
                  <table className="w-full text-left">
                    <thead className="sticky top-0 bg-ink-50 text-[11px] uppercase tracking-wider text-ink-500">
                      <tr>
                        <th className="px-4 py-2 font-semibold">Row</th>
                        <th className="px-4 py-2 font-semibold">Data in that row</th>
                        <th className="px-4 py-2 font-semibold">What's wrong</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                      {sheetIssues.issues.map((i) => (
                        <tr key={i.rowNumber} className="align-top">
                          <td className="whitespace-nowrap px-4 py-2.5">
                            <span className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-[11.5px] font-semibold text-red-700">
                              {i.rowNumber}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            {Object.entries(i.identity).length === 0 ? (
                              <span className="text-[12.5px] text-ink-400">
                                (row is empty)
                              </span>
                            ) : (
                              Object.entries(i.identity).map(([k, v]) => (
                                <div
                                  key={k}
                                  className="font-mono text-[12px] text-ink-600"
                                >
                                  <span className="text-ink-400">{k}=</span>
                                  {v}
                                </div>
                              ))
                            )}
                          </td>
                          <td className="px-4 py-2.5">
                            <ul className="space-y-1">
                              {i.problems.map((p, k) => (
                                <li
                                  key={k}
                                  className="text-[12.5px] leading-relaxed text-red-700"
                                >
                                  • {p}
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="border-t border-ink-200 bg-ink-50/60 px-4 py-2.5 text-[12.5px] text-ink-500">
                  Row numbers match your spreadsheet (header is row 1). Fix these
                  and upload the sheet again.
                </p>
              </Card>
            )}
          </div>
        )}

        {/* -------------------------------------------------- PUSH */}
        {tab === "push" && (
          <div className="animate-fade-in space-y-4">
            <Card className="flex flex-wrap items-center gap-3 p-4">
              <Button
                size="lg"
                variant="success"
                icon={SendIcon}
                onClick={handlePush}
                loading={pushing}
                disabled={!uploaded}
              >
                {pushing ? "Pushing…" : "Push to Naqood"}
              </Button>

              {pushing && (
                <div className="flex items-center gap-2 text-[13px] text-ink-500">
                  <Spinner size={14} />
                  <span>
                    Running for {elapsed}s — sending rows one at a time, please keep
                    this tab open.
                  </span>
                </div>
              )}

              {!pushing && result && (
                <span className="text-[12.5px] text-ink-500">
                  Saved to History — you can close this page.
                </span>
              )}
            </Card>

            {result && (
              <>
                <OrphanBanner orphaned={result.orphaned} />
                <RunProblemBanner status={result.status} error={result.error} />

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                  <Stat label="Rows in sheet" value={result.totalRows} />
                  <Stat label="Already synced" value={result.alreadySynced} tone="muted" />
                  <Stat label="Attempted" value={result.attempted} />
                  <Stat label="Pushed" value={result.success} tone="success" />
                  <Stat label="Failed" value={result.failed.length}
                    tone={result.failed.length ? "danger" : "neutral"} />
                </div>

                {result.attempted > 0 && (
                  <div className="h-2 overflow-hidden rounded-full bg-ink-200">
                    <div className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                      style={{ width: `${(result.success / result.attempted) * 100}%` }} />
                  </div>
                )}

                <PushLog
                  result={result}
                  onCopy={copyLog}
                  onDownload={downloadLog}
                  onVoid={VOID_CONFIG[type] ? handleVoid : undefined}
                  voidedIds={voidedIds}
                  voidingId={voidingId}
                  actionLabel={VOID_CONFIG[type]?.mode === "delete" ? "Delete" : "Void"}
                />

                {/* Every push refreshes the lookup tables first (see
                    ensureReferenceData). Saying so here is the only way users
                    learn it — the "Refresh reference data" button otherwise
                    reads like a step they were supposed to remember. */}
                <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-ink-500">
                  <span className="mt-0.5 shrink-0 text-ink-400">
                    <RefreshIcon size={13} />
                  </span>
                  <span>
                    Accounts, contacts, VAT codes, bank accounts and items were
                    pulled fresh from Naqood before this push — you never need to
                    refresh them by hand first.
                  </span>
                </p>

                {result.failed.length > 0 && (
                  <p className="text-[12.5px] leading-relaxed text-ink-500">
                    Failed rows were <span className="font-medium">not</span> created in
                    Naqood and stay unsynced — fix the cause and push again to retry
                    only those rows.
                  </p>
                )}
              </>
            )}

            {!result && !pushing && (
              <Card>
                <EmptyState
                  icon={SendIcon}
                  title="Ready to push"
                  description="Results and the full row-by-row log will appear here, and are saved to History."
                />
              </Card>
            )}
          </div>
        )}

        {/* ----------------------------------------------- HISTORY */}
        {tab === "history" && (
          <div className="animate-fade-in space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[13px] text-ink-500">
                {runs.length} saved push{runs.length === 1 ? "" : "es"} for this function
              </p>
              <Button size="sm" variant="secondary" icon={RefreshIcon} onClick={loadRuns}>
                Refresh
              </Button>
            </div>

            {runsLoading ? (
              <Skeleton className="h-40 w-full rounded-xl" />
            ) : runs.length === 0 ? (
              <Card>
                <EmptyState
                  icon={BookIcon}
                  title="No pushes yet"
                  description="Every push is recorded here with its full log, so you can review it later."
                />
              </Card>
            ) : (
              runs.map((r) => {
                const isOpen = openRun === r._id;
                const bad = r.status === "stuck" || r.status === "failed";
                // Void/delete runs (from the Void/Delete tab) share this same
                // History list with pushes — action tells them apart. Older
                // records predate the field and default to "push".
                const isUndo = r.action === "void" || r.action === "delete";
                const verb = r.action === "delete" ? "deleted" : r.action === "void" ? "voided" : "pushed";
                return (
                  <Card key={r._id} className="overflow-hidden">
                    <button
                      onClick={() => setOpenRun(isOpen ? null : r._id)}
                      className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition hover:bg-ink-50"
                    >
                      <span className={cx(
                        "grid h-9 w-9 shrink-0 place-items-center rounded-lg ring-1",
                        bad
                          ? "bg-amber-50 text-amber-600 ring-amber-100"
                          : r.failedCount
                          ? "bg-red-50 text-red-600 ring-red-100"
                          : isUndo
                          ? "bg-ink-100 text-ink-600 ring-ink-200"
                          : "bg-emerald-50 text-emerald-600 ring-emerald-100"
                      )}>
                        {bad ? <AlertIcon size={17} />
                          : r.failedCount ? <XIcon size={17} />
                          : isUndo ? <TrashIcon size={16} />
                          : <CheckIcon size={17} strokeWidth={2.5} />}
                      </span>

                      <div className="min-w-0 flex-1">
                        <p className="text-[13.5px] font-medium text-ink-900">
                          {isUndo && (
                            <span className="mr-1.5 rounded bg-ink-100 px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-500">
                              {r.action}
                            </span>
                          )}
                          {r.success} {verb}
                          {r.failedCount ? ` · ${r.failedCount} failed` : ""}
                          {bad ? ` · ${r.status}` : ""}
                        </p>
                        <p className="mt-0.5 text-[12px] text-ink-500">
                          {fmtWhen(r.startedAt)}
                          {r.durationMs != null && ` · took ${fmtDuration(r.durationMs)}`}
                          {r.orgSlug && ` · ${r.orgSlug}`}
                        </p>
                      </div>

                      <ChevronRightIcon
                        size={16}
                        className={cx("shrink-0 text-ink-400 transition", isOpen && "rotate-90")}
                      />
                    </button>

                    {isOpen && (
                      <div className="space-y-3 border-t border-ink-200/70 bg-ink-50/40 p-4">
                        <OrphanBanner orphaned={r.orphaned} />
                        <RunProblemBanner status={r.status} error={r.error} />
                        <div className={cx("grid grid-cols-2 gap-2", isUndo ? "sm:grid-cols-3" : "sm:grid-cols-5")}>
                          {!isUndo && <Stat label="Rows" value={r.totalRows} />}
                          {!isUndo && <Stat label="Synced before" value={r.alreadySynced} tone="muted" />}
                          <Stat label="Attempted" value={r.attempted} />
                          <Stat label={isUndo ? (r.action === "delete" ? "Deleted" : "Voided") : "Pushed"} value={r.success} tone="success" />
                          <Stat label="Failed" value={r.failedCount}
                            tone={r.failedCount ? "danger" : "neutral"} />
                        </div>
                        {buildLog(r).length > 0 && (
                          <PushLog
                            result={r}
                            onCopy={copyLog}
                            onDownload={downloadLog}
                            dense
                            onVoid={VOID_CONFIG[type] ? handleVoid : undefined}
                            voidedIds={voidedIds}
                            voidingId={voidingId}
                            actionLabel={VOID_CONFIG[type]?.mode === "delete" ? "Delete" : "Void"}
                          />
                        )}
                      </div>
                    )}
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* -------------------------------------------------- VOID (bulk) */}
        {tab === "void" && (() => {
          const cfg = VOID_CONFIG[type] || {};
          const isDelete = cfg.mode === "delete";
          const verb = isDelete ? "deleted" : "voided";
          return (
          <div className="mx-auto max-w-3xl animate-fade-in space-y-4">
            <Card className="p-6">
              <p className="text-[13.5px] font-medium text-ink-900">
                1. Download the rows that can be {verb}
              </p>
              <p className="mt-1 text-[12.5px] text-ink-500">
                Every pushed row not already {verb}, with its {cfg.refLabel} filled
                in. Delete any row you do <span className="font-medium">not</span> want
                {" "}{verb}, then upload what's left.
              </p>
              <Button
                className="mt-3"
                size="sm"
                variant="secondary"
                icon={DownloadIcon}
                loading={voidListDownloading}
                onClick={downloadVoidList}
              >
                Download {verb} rows
              </Button>
            </Card>

            <Card className="p-6">
              <p className="text-[13.5px] font-medium text-ink-900">
                2. Upload the trimmed sheet to {isDelete ? "delete" : "void"} them
              </p>
              <p className="mt-1 text-[12.5px] text-ink-500">
                Rows are matched back to Naqood by {cfg.refLabel}. Each one is{" "}
                {isDelete
                  ? "genuinely removed from Naqood — this is a real delete, not a reversible action."
                  : "voided in Naqood — nothing is erased, but this cannot be undone from here."}
              </p>

              <div
                onDragEnter={(e) => { e.preventDefault(); setVoidBulkDragging(true); }}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={() => setVoidBulkDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setVoidBulkDragging(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) setVoidBulkFile(f);
                }}
                onClick={() => voidBulkInputRef.current?.click()}
                className={cx(
                  "mt-3 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition",
                  voidBulkDragging
                    ? "border-brand-400 bg-brand-50"
                    : voidBulkFile
                    ? "border-brand-300 bg-brand-50/40"
                    : "border-ink-300 bg-ink-50/50 hover:border-brand-300 hover:bg-brand-50/30"
                )}
              >
                <input ref={voidBulkInputRef} type="file" accept=".xls,.xlsx,.csv"
                  className="hidden"
                  onChange={(e) => setVoidBulkFile(e.target.files[0])} />
                {voidBulkFile ? (
                  <>
                    <p className="text-[14px] font-medium text-ink-900">{voidBulkFile.name}</p>
                    <p className="mt-0.5 text-[12.5px] text-ink-500">
                      {(voidBulkFile.size / 1024).toFixed(0)} KB · click to change
                    </p>
                  </>
                ) : (
                  <p className="text-[13.5px] text-ink-600">
                    Drop the trimmed sheet here, or click to browse
                  </p>
                )}
              </div>

              <div className="mt-4 flex items-center gap-2.5">
                <Button
                  variant="danger"
                  icon={TrashIcon}
                  onClick={handleVoidBulkUpload}
                  loading={voidBulkUploading}
                  disabled={!voidBulkFile}
                >
                  {voidBulkUploading
                    ? (isDelete ? "Deleting…" : "Voiding…")
                    : (isDelete ? "Delete these rows" : "Void these rows")}
                </Button>
                {voidBulkFile && !voidBulkUploading && (
                  <Button variant="ghost" icon={XIcon} onClick={() => setVoidBulkFile(null)}>
                    Clear
                  </Button>
                )}
              </div>
            </Card>

            {voidBulkResult && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="Attempted" value={voidBulkResult.attempted} />
                  <Stat label={isDelete ? "Deleted" : "Voided"} value={voidBulkResult.success} tone="success" />
                  <Stat label="Failed" value={voidBulkResult.failed?.length || 0}
                    tone={voidBulkResult.failed?.length ? "danger" : "neutral"} />
                </div>
                <PushLog result={voidBulkResult} onCopy={copyLog} onDownload={downloadLog} />
              </>
            )}
          </div>
          );
        })()}
      </main>
    </AppShell>
  );
}
